import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  Shield,
  Home,
  Droplets,
  Flower2,
  Sun,
  Building,
  CloudRain,
  Settings,
  Car,
  Bath,
  ArrowRight,
  Layers,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSolutions, resolveMedia } from "@/lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

const iconMap = {
  home: Home,
  droplets: Droplets,
  "flower-2": Flower2,
  sun: Sun,
  building: Building,
  "cloud-rain": CloudRain,
  settings: Settings,
  car: Car,
  bath: Bath,
};

function LayerList({ layers = [] }) {
  if (!layers.length) return null;

  return (
    <div className="mt-4">
      <h5 className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-3">
        Capas del sistema
      </h5>

      <div className="space-y-2">
        {layers.map((layer, i) => (
          <div key={i} className="flex items-center gap-3 text-sm">
            <span className="w-7 h-7 bg-emerald-700 text-white text-[11px] font-bold flex items-center justify-center shrink-0">
              {i === 0 ? "S" : i}
            </span>
            <span className="text-zinc-700">
              {layer.name || layer.description || layer}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApplicationSteps({ steps = [] }) {
  const cleanSteps = (steps || []).filter(
    (step) => step?.image_url || step?.description || step?.number || step?.title
  );

  if (!cleanSteps.length) return null;

  return (
    <div className="mt-8 border-t border-zinc-200 pt-7">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 bg-red-700 flex items-center justify-center">
          <ImageIcon size={17} className="text-white" />
        </div>
        <div>
          <h4 className="font-heading text-xl text-zinc-900">
            Aplicación del sistema
          </h4>
          <p className="text-xs text-zinc-500">
            Secuencia administrable de imágenes, número y descripción.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {cleanSteps.map((step, i) => (
          <article key={i} className="bg-[#f7f2ee] border border-zinc-200 overflow-hidden">
            <div className="relative h-48 bg-zinc-100">
              {step.image_url ? (
                <img
                  src={resolveMedia(step.image_url)}
                  alt={step.title || `Paso ${step.number || i + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-300">
                  <ImageIcon size={34} />
                </div>
              )}

              <span className="absolute bottom-2 left-2 text-3xl font-heading text-white drop-shadow-lg">
                {step.number || i + 1}
              </span>
            </div>

            <div className="p-4 text-center">
              {step.title && (
                <h5 className="text-xs font-bold text-zinc-900 mb-1 uppercase tracking-wide">
                  {step.title}
                </h5>
              )}
              {step.description && (
                <p className="text-xs text-zinc-700 leading-snug">
                  {step.description}
                </p>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function SolutionSystemCard({ solution, subcategory, system, index }) {
  const Icon = iconMap[solution.icon] || Shield;
  const image = system.image_url || subcategory.image_url || solution.image_url;

  return (
    <motion.article
      variants={fadeUp}
      className="bg-white border border-zinc-200 overflow-hidden grid grid-cols-1 lg:grid-cols-5"
      data-testid={`solution-system-card-${index}`}
    >
      <div className="lg:col-span-2 bg-zinc-50 min-h-[260px] relative flex items-center justify-center">
        {image ? (
          <img
            src={resolveMedia(image)}
            alt={system.name || solution.title}
            className="w-full h-full max-h-[360px] object-contain p-4"
          />
        ) : (
          <div className="h-[260px] w-full bg-gradient-to-br from-zinc-100 to-zinc-200 flex items-center justify-center">
            <Icon size={46} className="text-zinc-300" />
          </div>
        )}

        <div className="absolute top-4 left-4 bg-emerald-700 p-2.5">
          <Icon size={18} className="text-white" />
        </div>
      </div>

      <div className="lg:col-span-3 p-6 lg:p-8">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-1">
            {solution.title}
          </span>

          {subcategory?.name && subcategory.name !== solution.title && (
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500 bg-zinc-100 px-2 py-1">
              {subcategory.name}
            </span>
          )}
        </div>

        <h3 className="font-heading text-2xl text-zinc-900 mb-3">
          {system.name || subcategory.name || solution.title}
        </h3>

        {(system.description || subcategory.description || solution.description) && (
          <p className="text-zinc-600 text-sm leading-relaxed mb-4">
            {system.description || subcategory.description || solution.description}
          </p>
        )}

        {system.materials?.length > 0 && (
          <div className="mb-4">
            <h5 className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-2">
              Materiales relacionados
            </h5>
            <div className="flex flex-wrap gap-2">
              {system.materials.map((mat, i) => (
                <span
                  key={i}
                  className="text-xs bg-zinc-100 text-zinc-700 px-2.5 py-1 border border-zinc-200"
                >
                  {mat}
                </span>
              ))}
            </div>
          </div>
        )}

        <LayerList layers={system.layers || []} />

        <div className="pt-6">
          <Link to="/contacto">
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-none font-bold tracking-wide">
              SOLICITAR ASESORIA <ArrowRight className="ml-2" size={16} />
            </Button>
          </Link>
        </div>
      </div>
    </motion.article>
  );
}

function buildSolutionCards(solution) {
  const cards = [];

  (solution.subcategories || []).forEach((sub) => {
    const systems = sub.systems || [];

    if (systems.length) {
      systems.forEach((sys) => cards.push({ subcategory: sub, system: sys }));
    } else if (sub.image_url || solution.image_url || sub.description) {
      cards.push({
        subcategory: sub,
        system: {
          name: sub.name,
          description: sub.description || solution.description,
          image_url: sub.image_url || solution.image_url,
          layers: [],
          materials: [],
        },
      });
    }
  });

  return cards;
}

function SolutionBlock({ solution, index }) {
  const cards = buildSolutionCards(solution);
  const Icon = iconMap[solution.icon] || Shield;

  return (
    <motion.section
      key={solution.id || solution.slug || index}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      <div className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-700 bg-white border border-emerald-100 px-3 py-2 mb-3">
            <Icon size={14} />
            Solución {String(index + 1).padStart(2, "0")}
          </div>
          <h2 className="font-heading text-3xl text-zinc-900">{solution.title}</h2>
          {solution.description && (
            <p className="text-zinc-600 text-sm leading-relaxed max-w-3xl mt-2">
              {solution.description}
            </p>
          )}
        </div>
      </div>

      <motion.div initial="hidden" animate="visible" className="space-y-6">
        {cards.map((card, i) => (
          <SolutionSystemCard
            key={`${solution.slug}-${card.subcategory.name}-${card.system.name}-${i}`}
            solution={solution}
            subcategory={card.subcategory}
            system={card.system}
            index={i}
          />
        ))}
      </motion.div>

      <ApplicationSteps steps={solution.application_steps || []} />
    </motion.section>
  );
}

export default function SolutionsPage() {
  const [solutions, setSolutions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const location = useLocation();

  useEffect(() => {
    getSolutions()
      .then((r) => setSolutions(r.data || []))
      .catch(() => {});
  }, []);

  const ordered = useMemo(
    () => [...solutions].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [solutions]
  );

  useEffect(() => {
    if (!ordered.length) return;
    const params = new URLSearchParams(location.search);
    const slug = params.get("solution");
    if (!slug) return;
    const idx = ordered.findIndex((s) => s.slug === slug);
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [ordered, location.search]);

  useEffect(() => {
    if (ordered.length && activeIndex > ordered.length - 1) {
      setActiveIndex(0);
    }
  }, [ordered.length, activeIndex]);

  const activeSolution = ordered[activeIndex];
  const activeCards = activeSolution ? buildSolutionCards(activeSolution) : [];

  const goPrev = () => {
    if (!ordered.length) return;
    setActiveIndex((current) =>
      current === 0 ? ordered.length - 1 : current - 1
    );
  };

  const goNext = () => {
    if (!ordered.length) return;
    setActiveIndex((current) =>
      current === ordered.length - 1 ? 0 : current + 1
    );
  };

  return (
    <main data-testid="solutions-page" className="pt-20 lg:pt-24 bg-construtop-texture">
      <section className="bg-zinc-950 py-20 lg:py-28 section-padding relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-emerald-400 text-sm font-bold uppercase tracking-[0.2em] mb-3"
          >
            Soluciones por tipo de obra
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl sm:text-5xl text-white mb-4"
          >
            Sistemas aplicados a necesidades reales de obra
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-zinc-400 max-w-2xl text-base"
          >
            Seleccione una etiqueta para ver una solución a la vez. Cada bloque permite mostrar sistemas relacionados y una secuencia de aplicación administrable.
          </motion.p>
        </div>
      </section>

      <section className="sticky top-16 lg:top-20 z-30 bg-white/95 backdrop-blur border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex flex-wrap gap-2">
          {ordered.map((solution, index) => (
            <button
              key={solution.id || solution.slug || index}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border transition-colors whitespace-nowrap ${
                activeIndex === index
                  ? "border-emerald-700 text-emerald-800 bg-emerald-50"
                  : "border-zinc-200 text-zinc-600 hover:border-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
              }`}
            >
              {solution.title}
            </button>
          ))}
        </div>
      </section>

      <section className="section-spacing section-padding bg-construtop-texture">
        <div className="max-w-7xl mx-auto">
          {activeSolution ? (
            <>
              <div className="mb-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-3 text-sm text-zinc-500">
                  <Layers size={18} className="text-emerald-700" />
                  <span>
                    Mostrando {activeIndex + 1} de {ordered.length}: {activeSolution.title}
                  </span>
                  <span className="hidden sm:inline text-zinc-300">|</span>
                  <span className="hidden sm:inline">
                    {activeCards.length} especificaciones disponibles
                  </span>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={goPrev}
                    className="rounded-none border-zinc-300 text-zinc-700"
                  >
                    <ChevronLeft size={16} className="mr-1" />
                    ANTERIOR
                  </Button>

                  <Button
                    type="button"
                    onClick={goNext}
                    className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white"
                  >
                    SIGUIENTE
                    <ChevronRight size={16} className="ml-1" />
                  </Button>
                </div>
              </div>

              <SolutionBlock
                solution={activeSolution}
                index={activeIndex}
              />
            </>
          ) : (
            <div className="py-20 text-center text-zinc-500">
              Contenido en proceso de carga.
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
